import Size from '../data/Size';
import { Resolution } from '../data/Resolution';
import BackgroundStyle from '../data/BackgroundStyle';
import { PointData, PolygonShapeData, ShapeData } from 'data/FlyerData';

export interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const getColorBoundsRect = (img: HTMLImageElement): Rectangle => {
  if (img.width === 0 || img.height === 0) {
    console.error(`Image had no size (${img.width},${img.height}`);
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  const w = img.width;
  const h = img.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h).data;
  let x = 0;
  let y = 0;
  let foundAlpha = false;
  const result: Rectangle = {
    x: 0,
    y: 0,
    width: w,
    height: h
  };
  // top
  for (y = 0; y < h; y++) {
    foundAlpha = false;
    for (x = 0; x < w; x++) {
      const index = (x + y * w) * 4;
      const a = data[index + 3];
      if (a > 0) {
        result.y = y;
        foundAlpha = true;
        break;
      }
    }
    if (foundAlpha) {
      break;
    }
  }
  // left
  for (x = 0; x < w; x++) {
    foundAlpha = false;
    for (y = 0; y < h; y++) {
      const index = (x + y * w) * 4;
      const a = data[index + 3];
      if (a > 0) {
        result.x = x;
        foundAlpha = true;
        break;
      }
    }
    if (foundAlpha) {
      break;
    }
  }
  // right
  for (x = w - 1; x >= 0; x--) {
    foundAlpha = false;
    for (y = 0; y < h; y++) {
      const index = (x + y * w) * 4;
      const a = data[index + 3];
      if (a > 0) {
        result.width = x + 1 - result.x;
        foundAlpha = true;
        break;
      }
    }
    if (foundAlpha) {
      break;
    }
  }
  // bottom
  for (y = h - 1; y >= 0; y--) {
    foundAlpha = false;
    for (x = 0; x < w; x++) {
      const index = (x + y * w) * 4;
      const a = data[index + 3];
      if (a > 0) {
        result.height = y + 1 - result.y;
        foundAlpha = true;
        break;
      }
    }
    if (foundAlpha) {
      break;
    }
  }
  return result;
};

export const trimImage = (
  img: HTMLImageElement
): HTMLImageElement | HTMLCanvasElement => {
  const boundsRect = getColorBoundsRect(img);
  // console.log(boundsRect);
  if (boundsRect.width === img.width && boundsRect.height === img.height) {
    return img;
  }
  const canvas = document.createElement('canvas');
  canvas.width = boundsRect.width;
  canvas.height = boundsRect.height;
  const ctx: CanvasRenderingContext2D = canvas.getContext('2d')!;
  ctx.drawImage(img, -boundsRect.x, -boundsRect.y, img.width, img.height);
  return canvas;
};

export const getBackgroundStyleFromImage = (
  img: HTMLImageElement
): BackgroundStyle => {
  // const MINIATURE_SIZE = 100;
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const context = canvas.getContext('2d');
  context!.drawImage(img, 0, 0, img.width, img.height);
  const data = context!.getImageData(0, 0, img.width, img.height).data;
  let visiblesCount = 0;
  let r = 0;
  let g = 0;
  let b = 0;
  let alpha = 0;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a === 255) {
      visiblesCount++;
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
    }
    alpha += a;
  }
  let type = BackgroundStyle.Opaque;
  if (alpha === 0) {
    type = BackgroundStyle.Transparent;
  } else if (alpha / (img.width * img.height) < 255) {
    r /= visiblesCount * 255;
    g /= visiblesCount * 255;
    b /= visiblesCount * 255;
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    type = BackgroundStyle.Transparent;
    if (luminance >= 0.66) {
      type = BackgroundStyle.TransparentLight;
    } else if (luminance <= 0.33) {
      type = BackgroundStyle.TransparentDark;
    }
  }
  // console.log( 'parsed image type in ' + (Date.now()-time) + 'ms');
  return type;
};

export const createJpegFileFromCanvas = (
  canvas: HTMLCanvasElement,
  filename: string = 'image.jpg'
): Promise<File> => {
  return new Promise((resolve: (f: File) => void) => {
    canvas.toBlob(
      (blob: Blob) => {
        resolve(new File([blob], filename));
      },
      'image/jpeg',
      0.6
    );
  });
};

export const createPngFileFromCanvas = (
  canvas: HTMLCanvasElement,
  filename: string = 'image.png'
): Promise<File> => {
  return new Promise((resolve: (f: File) => void) => {
    canvas.toBlob((blob: Blob) => {
      resolve(new File([blob], filename));
    }, 'image/png');
  });
};

export const createCanvasFromImage = (
  image: HTMLImageElement,
  width: number,
  height: number
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  const x = (width - image.width) * 0.5;
  const y = (height - image.height) * 0.5;
  ctx.fillStyle = 'black';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image, x, y);
  return canvas;
};

// export const addBorderToSmallImage = (
//   url: string,
//   minWidth: number,
//   minHeight: number,
//   callback: (newImageUrl: string) => void
// ) => {
//   const img = new Image();
//   img.crossOrigin = 'Anonymous';
//   img.onload = () => {
//     if (img.width >= minWidth && img.height >= minHeight) {
//       callback(url);
//       return;
//     }
//     const canvas = document.createElement('canvas');
//     canvas.width = Math.max(img.width, minWidth);
//     canvas.height = Math.max(img.height, minHeight);
//     const context = canvas.getContext('2d');
//     context!.drawImage(
//       img,
//       (canvas.width - img.width) / 2,
//       (canvas.height - img.height) / 2,
//       img.width,
//       img.height
//     );
//     canvas.toBlob(blob => {
//       callback(URL.createObjectURL(blob));
//     });
//   };
//   img.src = url;
// };

export const getImageSizeFromURL = async (url: string): Promise<Size> => {
  return new Promise((resolve: (size: Size) => void) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.src = url;
  });
};

export const getImageSizeFromFile = async (file: File): Promise<Size> => {
  return new Promise((resolve: (size: Size) => void) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    const reader = new FileReader();
    reader.onload = () => {
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
};

export const getStageScreenshot = (
  resolution: Resolution,
  renderer: any,
  stage: any,
  size: Size | null = null
): HTMLCanvasElement => {
  if (!renderer) {
    throw new Error('Cannot take a screenshot because the renderer is not set');
  }
  if (!stage) {
    throw new Error('Cannot take a screenshot because the stage is not set');
  }

  let tempTexture = PIXI.RenderTexture.create(
    resolution.width,
    resolution.height
  );
  renderer.render(stage, tempTexture);
  let canvas: HTMLCanvasElement = renderer.plugins.extract.canvas(tempTexture);
  tempTexture.destroy();

  if (
    size &&
    (size.width !== resolution.width || size.height !== resolution.height)
  ) {
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = size.width;
    resizedCanvas.height = size.height;
    const context = resizedCanvas.getContext('2d')!;
    const scale = Math.max(
      size.width / resolution.width,
      size.height / resolution.height
    );
    const scaledWidth = resolution.width * scale;
    const scaledHeight = resolution.height * scale;
    if ((context as any).imageSmoothingQuality) {
      (context as any).imageSmoothingQuality = 'high';
    }
    context.drawImage(
      canvas,
      (size.width - scaledWidth) / 2,
      (size.height - scaledHeight) / 2,
      scaledWidth,
      scaledHeight
    );
    canvas = resizedCanvas;
  }
  return canvas;
};

export const getStageScreenshotFile = async (
  resolution: Resolution,
  renderer: any,
  stage: any,
  size: Size | null = null
): Promise<File> => {
  const canvas = getStageScreenshot(resolution, renderer, stage, size);
  return await createJpegFileFromCanvas(canvas);
};

export const isExtensionImage = (extension: string): boolean => {
  return ['jpeg', 'jpg', 'png'].indexOf(extension.toLowerCase()) > -1;
};

export const getStageLuminosity = (
  resolution: Resolution,
  renderer: any,
  stage: any
): number => {
  const width = 10;
  const height = 10;
  const canvas = getStageScreenshot(resolution, renderer, stage, {
    width,
    height
  });
  const context = canvas.getContext('2d');
  const data = context!.getImageData(0, 0, width, height).data;
  let r = 0;
  let g = 0;
  let b = 0;
  let totalLuminance = 0;
  for (let i = 0; i < data.length; i += 4) {
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    totalLuminance += luminance;
  }
  totalLuminance /= data.length / 4;
  return totalLuminance / 50;
};

export const createCanvasWithGradient = (
  width: number,
  height: number,
  colors: string[]
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    if (colors.length > 1) {
      const grd = ctx.createLinearGradient(0, 0, 0, height);
      grd.addColorStop(0, colors[0]);
      for (let i = 1; i < colors.length - 1; i++) {
        grd.addColorStop(i / (colors.length - 1), colors[i]);
      }
      grd.addColorStop(1, colors[colors.length - 1]);

      ctx.fillStyle = grd;
    } else {
      ctx.fillStyle = colors[0];
    }
    ctx.fillRect(0, 0, width, height);
  }

  return canvas;
};

const drawRoundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  cornerRadius: number = 0
) => {
  const radius = {
    tl: cornerRadius,
    tr: cornerRadius,
    br: cornerRadius,
    bl: cornerRadius
  };
  ctx.moveTo(x + radius.tl, y);
  ctx.lineTo(x + width - radius.tr, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
  ctx.lineTo(x + width, y + height - radius.br);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - radius.br,
    y + height
  );
  ctx.lineTo(x + radius.bl, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
  ctx.lineTo(x, y + radius.tl);
  ctx.quadraticCurveTo(x, y, x + radius.tl, y);
};

export const getShapeRectangle = (shape: ShapeData): Rectangle => {
  let x = 0;
  let y = 0;
  let width = 0;
  let height = 0;
  if (Array.isArray(shape)) {
    let p = shape[0];
    x = p.x;
    let right = p.x;
    y = p.y;
    let bottom = p.y;
    shape.forEach(s => {
      x = Math.min(x, s.x);
      right = Math.max(right, s.x);
      y = Math.min(y, s.y);
      bottom = Math.max(bottom, s.y);
    });
    width = right - x;
    height = bottom - y;
  } else {
    x = shape.x;
    y = shape.y;
    width = shape.width;
    height = shape.height;
  }
  return {
    x,
    y,
    width,
    height
  };
};

export const getShapePIXIRectangle = (shape: ShapeData): PIXI.Rectangle => {
  const { x, y, width, height } = getShapeRectangle(shape);
  return new PIXI.Rectangle(x, y, width, height);
};

const DEG_TO_RAD = 0.0174533;

export const getPolygonPoints = (polygon: PolygonShapeData): PointData[] => {
  const w2 = polygon.width / 2;
  const h2 = polygon.height / 2;
  const cx = polygon.x + w2;
  const cy = polygon.y + h2;

  const arc = (360 / polygon.corners) * DEG_TO_RAD;
  let angle = -90 * DEG_TO_RAD;
  const points: PointData[] = [];
  for (let i = 0; i < polygon.corners; i++) {
    points[i] = {
      x: cx + w2 * Math.cos(angle),
      y: cy + h2 * Math.sin(angle)
    };
    angle += arc;
  }
  return points;
};

export const createCanvasWithShape = (
  shape: ShapeData,
  hole: ShapeData | undefined = undefined
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = 0;
  canvas.height = 0;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }
  let top = 0;
  let left = 0;
  ctx.beginPath();

  const isPolygon = 'corners' in shape;
  if (Array.isArray(shape) || isPolygon) {
    const points = isPolygon ? getPolygonPoints(shape) : shape;
    let p = points[0];
    if (isPolygon) {
      top = shape.y;
      left = shape.x;
      canvas.width = shape.width;
      canvas.height = shape.height;
    } else {
      left = p.x;
      let right = p.y;
      top = p.y;
      let bottom = p.y;
      points.forEach(s => {
        left = Math.min(left, s.x);
        right = Math.max(right, s.x);
        top = Math.min(top, s.y);
        bottom = Math.max(bottom, s.y);
      });
      canvas.width = right - left;
      canvas.height = bottom - top;
    }
    p = points[0];
    ctx.moveTo(p.x - left, p.y - top);
    for (var i = 1; i < points.length; i++) {
      p = points[i];
      ctx.lineTo(p.x - left, p.y - top);
    }
    p = points[0];
    ctx.lineTo(p.x - left, p.y - top);
  } else if (shape.isOval) {
    top = shape.y;
    left = shape.x;
    canvas.width = shape.width;
    canvas.height = shape.height;
    ctx.ellipse(
      shape.width * 0.5,
      shape.height * 0.5,
      shape.width * 0.5,
      shape.height * 0.5,
      0,
      0,
      Math.PI * 2
    );
  } else {
    top = shape.y;
    left = shape.x;
    canvas.width = shape.width;
    canvas.height = shape.height;
    drawRoundRect(ctx, 0, 0, shape.width, shape.height, shape.cornerRadius);
  }

  if (hole) {
    if (Array.isArray(hole)) {
      let p = hole[hole.length - 1];
      ctx.moveTo(p.x - left, p.y - top);
      for (var j = hole.length - 2; j >= 0; j--) {
        p = hole[j];
        ctx.lineTo(p.x - left, p.y - top);
      }
    } else if ('corners' in hole) {
      // TODO: draw polygon
    } else if (hole.isOval) {
      ctx.ellipse(
        hole.x - left + hole.width * 0.5,
        hole.y - top + hole.height * 0.5,
        hole.width * 0.5,
        hole.height * 0.5,
        0,
        0,
        Math.PI * 2
      );
    } else {
      drawRoundRect(
        ctx,
        hole.x - left,
        hole.y - top,
        hole.width,
        hole.height,
        hole.cornerRadius
      );
    }
  }

  ctx.closePath();
  ctx.fillStyle = '#00ff00';
  ctx.fill('evenodd');
  return canvas;
};

export const transformShape = (
  prevBox: PIXI.Rectangle,
  newBox: PIXI.Rectangle,
  shape: ShapeData
): ShapeData => {
  if (Array.isArray(shape)) {
    // transform points
    const left = prevBox.x;
    const top = prevBox.y;
    const dx = newBox.x - prevBox.x;
    const dy = newBox.y - prevBox.y;
    const sx = newBox.width / prevBox.width;
    const sy = newBox.height / prevBox.height;
    return shape.map(p => {
      // scale
      let x = left + (p.x - left) * sx;
      let y = top + (p.y - top) * sy;
      // move
      x += dx;
      y += dy;
      return { x, y };
    });
  } else {
    const { x, y, width, height } = newBox;
    return {
      ...shape,
      x,
      y,
      width,
      height
    };
  }
};



// WEBPACK FOOTER //
// ./src/utils/GraphicsUtils.ts